/**
 * A page's code that failed to load (1.3.0 Help fix). Guides, and other parts loaded when first opened, come as
 * separate modules; one dropped request (a restarted dev server, a new version deployed with new file names, a
 * network hiccup) rejects that import, and the browser remembers the failure for the rest of the page's life, so
 * only a reload fetches it again. The error boundary reloads once by itself, and never twice for the same address
 * within RELOAD_WINDOW_MS, so a module that keeps failing shows its error instead of reloading in a loop.
 */

/** The messages browsers give a dynamic import that could not be fetched (Chrome, Firefox, Safari, Vite's preload). */
const CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading chunk [\w-]+ failed/i;

export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return CHUNK_ERROR.test(message);
}

export const RELOAD_WINDOW_MS = 30_000;
const KEY = 'prism.chunkReload';

interface KeyValueStore { getItem(key: string): string | null; setItem(key: string, value: string): void }

/**
 * Whether to reload `href` now for a module that failed to load, recording it when so: true the first time, false
 * when this address was already reloaded for that reason less than RELOAD_WINDOW_MS ago, or when nothing can be
 * recorded (without a record there is no way to stop a loop).
 */
export function claimChunkReload(store: KeyValueStore | null, href: string, now = Date.now()): boolean {
  if (!store) return false;
  try {
    const last = JSON.parse(store.getItem(KEY) ?? 'null') as { href?: string; at?: number } | null;
    if (last?.href === href && typeof last.at === 'number' && now - last.at >= 0 && now - last.at < RELOAD_WINDOW_MS) return false;
    store.setItem(KEY, JSON.stringify({ href, at: now }));
    return true;
  } catch {
    return false;
  }
}
